import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { getStakeholderDetail } from '../../../../services/stakeholdersAPI';
import { Pill, Stacked, buildFormStyles, formatDate, money } from '../treasury/treasuryUi';
import { ROLE_CONFIG, ROLE_LABELS } from './stakeholderLabels';

const REGISTER_LABELS = {
  ventas: 'Ventas',
  compras: 'Compras',
  honorarios: 'Honorarios',
  remuneraciones: 'Remuneraciones',
};

const INCOME_COLOR = '#166534';
const EXPENSE_COLOR = '#9a3412';
const INCOME_COLOR_DARK = '#86efac';
const EXPENSE_COLOR_DARK = '#fdba74';

const STATUS_TONES = {
  Pagado: 'success',
  'Por vencer': 'warning',
  'Vencido por pagar': 'danger',
  Anulado: 'purple',
  'No aplica': 'info',
};

const compareValues = (left, right) => {
  if (left == null && right == null) return 0;
  if (left == null) return -1;
  if (right == null) return 1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), 'es', { numeric: true, sensitivity: 'base' });
};

const DetailField = ({ ui, label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: ui.muted }}>
      {label}
    </div>
    <div style={{ marginTop: 3, fontSize: 13, fontWeight: 600, color: ui.text, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

const cardStyle = (ui) => ({
  background: ui.panel,
  border: `1px solid ${ui.border}`,
  borderRadius: 12,
  overflow: 'hidden',
  minWidth: 0,
  minHeight: 0,
  height: '100%',
});

const MiniTable = ({ ui, columns, rows, emptyText, defaultSort }) => {
  const [sort, setSort] = useState(defaultSort || { key: columns[0]?.key, dir: 'desc' });

  const sortedRows = useMemo(() => {
    const column = columns.find((item) => item.key === sort.key) || columns[0];
    if (!column) return rows;
    const direction = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => {
      const leftValue = column.sortValue ? column.sortValue(left) : left[column.key];
      const rightValue = column.sortValue ? column.sortValue(right) : right[column.key];
      return compareValues(leftValue, rightValue) * direction;
    });
  }, [columns, rows, sort]);

  const toggleSort = (column) => {
    setSort((current) => {
      if (current.key === column.key) {
        return { key: column.key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: column.key, dir: column.defaultDir || 'desc' };
    });
  };

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      {!rows.length ? (
        <p style={{ margin: 16, fontSize: 12, color: ui.muted }}>{emptyText}</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sort.key === column.key;
                return (
                  <th
                    key={column.key}
                    onClick={() => toggleSort(column)}
                    style={{
                      position: 'sticky',
                      top: 0,
                      background: ui.panelAlt || ui.panel,
                      color: active ? ui.text : ui.muted,
                      fontWeight: 700,
                      textAlign: column.align || 'left',
                      padding: '8px 10px',
                      borderBottom: `1px solid ${ui.border}`,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
                        width: '100%',
                      }}
                    >
                      {column.label}
                      {active ? (
                        sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                      ) : (
                        <ChevronDown size={12} style={{ opacity: 0.35 }} />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.id}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${ui.border}`,
                      color: ui.text,
                      textAlign: column.align || 'left',
                      verticalAlign: 'top',
                    }}
                  >
                    {column.render ? column.render(row) : row[column.key] || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const TotalsBar = ({ ui, items }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 12,
      padding: '10px 12px',
      borderTop: `1px solid ${ui.border}`,
      background: ui.panelAlt || ui.panel,
      minHeight: 64,
      height: 64,
      boxSizing: 'border-box',
      flexShrink: 0,
      alignItems: 'center',
    }}
  >
    {items.map((item) => (
      <div key={item.label} style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: ui.muted }}>
          {item.label}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 13,
            fontWeight: 700,
            color: item.color || ui.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.value}
        </div>
      </div>
    ))}
  </div>
);

const ColumnCard = ({ ui, title, count, children, totals }) => (
  <section style={{ ...cardStyle(ui), display: 'flex', flexDirection: 'column' }}>
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 8,
        padding: '12px 14px 10px',
        borderBottom: `1px solid ${ui.border}`,
        flexShrink: 0,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>{title}</h3>
      <span style={{ fontSize: 11, color: ui.muted }}>{count} registro{count === 1 ? '' : 's'}</span>
    </header>
    {children}
    {totals}
  </section>
);

const signedMoney = (amount, { income, isDarkMode }) => {
  const value = Number(amount || 0);
  const positive = income ?? value >= 0;
  const formatted = money(Math.abs(value));
  return (
    <span style={{ fontWeight: 700, color: positive ? (isDarkMode ? INCOME_COLOR_DARK : INCOME_COLOR) : (isDarkMode ? EXPENSE_COLOR_DARK : EXPENSE_COLOR) }}>
      {positive ? `+${formatted}` : `-${formatted}`}
    </span>
  );
};

const StakeholderDetailModal = ({ ui, isDarkMode = false, role, stakeholder, onClose }) => {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.clientes;
  const styles = buildFormStyles(ui);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getStakeholderDetail({
      role,
      rut: stakeholder?.rut || '',
      name: stakeholder?.name || '',
    })
      .then((payload) => {
        if (!cancelled) setDetail(payload);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'No se pudo cargar el detalle de la contraparte.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [role, stakeholder?.id, stakeholder?.rut, stakeholder?.name]);

  const name = detail?.name || stakeholder?.name || 'Sin nombre';
  const rut = detail?.rut || stakeholder?.rut || '';
  const master = detail?.master || stakeholder?.master;
  const roles = detail?.roles || stakeholder?.roles || [];
  const otherRoles = roles.filter((item) => item !== role);
  const operations = detail?.operations || { title: 'Operaciones', items: [], totals: { count: 0, amount: 0 } };
  const documents = detail?.documents || { title: 'Documentos contables', items: [], totals: { count: 0, amount: 0 } };
  const movements = detail?.movements || { title: 'Movimientos bancarios', items: [], totals: { count: 0, ingresos: 0, egresos: 0, net: 0 } };

  const generalFields = [
    { label: 'Nombre', value: name },
    { label: 'RUT', value: rut || 'Sin RUT' },
    { label: 'Rol', value: ROLE_LABELS[role] },
    { label: config.book, value: `${stakeholder?.documents || 0} documento(s)` },
    { label: `${config.amountLabel} histórico`, value: money(stakeholder?.total_amount) },
    { label: 'Último documento', value: formatDate(stakeholder?.last_date) },
  ];

  if (master?.type === 'organization') {
    generalFields.push(
      { label: 'Código CRM', value: master.code },
      { label: 'Estado', value: master.status },
      { label: 'Segmento', value: master.segment },
      { label: 'Industria', value: master.industry },
      { label: 'Ubicación', value: [master.commune, master.region].filter(Boolean).join(', ') },
      { label: 'Email', value: master.email },
    );
  } else if (master?.type === 'supplier') {
    generalFields.push(
      { label: 'Código', value: master.code },
      { label: 'Razón social', value: master.business_name },
      { label: 'Tipo', value: master.supplier_type },
      { label: 'Estado', value: master.status },
      { label: 'Condición de pago', value: (master.payment_terms || []).join(', ') },
      { label: 'Email contacto', value: master.contacts?.[0]?.email },
    );
  } else if (master?.type === 'collaborator') {
    generalFields.push(
      { label: 'Código', value: master.code },
      { label: 'Cargo', value: master.job_title },
      { label: 'Área', value: master.department },
      { label: 'Estado', value: master.status },
      { label: 'Email', value: master.email },
      { label: 'Teléfono', value: master.phone },
    );
  }

  const operationEmpty = {
    purchase_orders: 'Sin órdenes de compra para este proveedor.',
    sales: 'Sin ventas para este cliente.',
    expense_reports: 'Sin rendiciones para este colaborador.',
    operations: 'Este tipo de contraparte no tiene órdenes de compra ni ventas.',
  }[operations.kind || 'operations'];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: ui.overlay || 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(1760px, calc(100vw - 16px))',
          maxHeight: '94vh',
          background: ui.page || ui.panel,
          border: `1px solid ${ui.border}`,
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
          display: 'grid',
          gridTemplateRows: 'auto auto minmax(420px, 1fr)',
          gap: 12,
          padding: 16,
          boxSizing: 'border-box',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: ui.text }}>{name}</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
              {[rut || 'Sin RUT', ROLE_LABELS[role]].filter(Boolean).join(' · ')}
            </p>
            {otherRoles.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {otherRoles.map((item) => (
                  <Pill key={item} label={`También ${ROLE_LABELS[item].toLowerCase()}`} tone="info" isDarkMode={isDarkMode} />
                ))}
              </div>
            ) : null}
          </div>
          <button type="button" style={styles.ghostBtn} onClick={onClose} aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        <section style={{ ...cardStyle(ui), height: 'auto', padding: 14 }}>
          <p style={{ ...styles.sectionTitle, marginTop: 0 }}>Información general</p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 14,
            }}
          >
            {generalFields.map((field) => (
              <DetailField key={field.label} ui={ui} label={field.label} value={field.value} />
            ))}
          </div>
        </section>

        {error ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{error}</p> : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 12,
            minHeight: 0,
            alignItems: 'stretch',
          }}
        >
          <ColumnCard
            ui={ui}
            title={operations.title}
            count={operations.items?.length || 0}
            totals={
              <TotalsBar
                ui={ui}
                items={[
                  { label: 'Registros', value: String(operations.totals?.count || 0) },
                  { label: 'Total c/IVA', value: money(operations.totals?.amount) },
                ]}
              />
            }
          >
            {loading ? (
              <p style={{ margin: 16, fontSize: 12, color: ui.muted, flex: 1 }}>Cargando operaciones…</p>
            ) : (
              <MiniTable
                ui={ui}
                emptyText={operationEmpty}
                defaultSort={{ key: 'date', dir: 'desc' }}
                columns={[
                  {
                    key: 'code',
                    label: 'Código',
                    sortValue: (row) => String(row.code || row.invoice_number || row.id).toLowerCase(),
                    render: (row) => (
                      <Stacked
                        top={row.code || row.invoice_number || `#${row.id}`}
                        bottom={row.invoice_number && row.code ? row.invoice_number : row.document_type}
                        topColor={ui.text}
                        muted={ui.muted}
                      />
                    ),
                  },
                  {
                    key: 'date',
                    label: 'Fecha',
                    defaultDir: 'desc',
                    sortValue: (row) => String(row.date || ''),
                    render: (row) => formatDate(row.date),
                  },
                  {
                    key: 'status',
                    label: 'Estado',
                    sortValue: (row) => String(row.status || '').toLowerCase(),
                  },
                  {
                    key: 'amount',
                    label: 'Monto c/IVA',
                    align: 'right',
                    defaultDir: 'desc',
                    sortValue: (row) => Number(row.amount || 0),
                    render: (row) => money(row.amount, row.currency),
                  },
                ]}
                rows={operations.items || []}
              />
            )}
          </ColumnCard>

          <ColumnCard
            ui={ui}
            title={documents.title}
            count={documents.items?.length || 0}
            totals={
              <TotalsBar
                ui={ui}
                items={[
                  { label: 'Documentos', value: String(documents.totals?.count || 0) },
                  { label: 'Total c/IVA', value: money(documents.totals?.amount) },
                ]}
              />
            }
          >
            {loading ? (
              <p style={{ margin: 16, fontSize: 12, color: ui.muted, flex: 1 }}>Cargando documentos…</p>
            ) : (
              <MiniTable
                ui={ui}
                emptyText="Sin documentos contables asociados a esta contraparte."
                defaultSort={{ key: 'date', dir: 'desc' }}
                columns={[
                  {
                    key: 'doc_number',
                    label: 'Documento',
                    sortValue: (row) => String(row.doc_number || row.id).toLowerCase(),
                    render: (row) => (
                      <Stacked
                        top={row.doc_number || `#${row.id}`}
                        bottom={[REGISTER_LABELS[row.register_type] || row.register_type, row.document_type]
                          .filter(Boolean)
                          .join(' · ')}
                        topColor={ui.text}
                        muted={ui.muted}
                      />
                    ),
                  },
                  {
                    key: 'date',
                    label: 'Fecha',
                    defaultDir: 'desc',
                    sortValue: (row) => String(row.date || ''),
                    render: (row) => formatDate(row.date),
                  },
                  {
                    key: 'status',
                    label: 'Estado',
                    sortValue: (row) => String(row.status || '').toLowerCase(),
                    render: (row) =>
                      row.status ? (
                        <Pill
                          label={row.status}
                          tone={STATUS_TONES[row.status] || 'info'}
                          isDarkMode={isDarkMode}
                        />
                      ) : (
                        '—'
                      ),
                  },
                  {
                    key: 'amount',
                    label: 'Total c/IVA',
                    align: 'right',
                    defaultDir: 'desc',
                    sortValue: (row) => Number(row.amount || 0),
                    render: (row) => money(row.amount),
                  },
                ]}
                rows={documents.items || []}
              />
            )}
          </ColumnCard>

          <ColumnCard
            ui={ui}
            title={movements.title}
            count={movements.items?.length || 0}
            totals={
              <TotalsBar
                ui={ui}
                items={[
                  { label: 'Movimientos', value: String(movements.totals?.count || 0) },
                  {
                    label: 'Neto',
                    value: signedMoney(movements.totals?.net, { isDarkMode }),
                  },
                ]}
              />
            }
          >
            {loading ? (
              <p style={{ margin: 16, fontSize: 12, color: ui.muted, flex: 1 }}>Cargando movimientos…</p>
            ) : (
              <MiniTable
                ui={ui}
                emptyText="Sin movimientos bancarios asociados a esta contraparte."
                defaultSort={{ key: 'date', dir: 'desc' }}
                columns={[
                  {
                    key: 'description',
                    label: 'Movimiento',
                    sortValue: (row) => String(row.description || '').toLowerCase(),
                    render: (row) => (
                      <Stacked
                        top={row.description || 'Movimiento'}
                        bottom={[row.bank_name, row.account_name, row.document_number].filter(Boolean).join(' · ')}
                        topColor={ui.text}
                        muted={ui.muted}
                      />
                    ),
                  },
                  {
                    key: 'date',
                    label: 'Fecha',
                    defaultDir: 'desc',
                    sortValue: (row) => String(row.date || ''),
                    render: (row) => formatDate(row.date),
                  },
                  {
                    key: 'amount',
                    label: 'Monto',
                    align: 'right',
                    defaultDir: 'desc',
                    sortValue: (row) => Number(row.signed_amount ?? (row.entry_type === 'income' ? row.amount : -row.amount) ?? 0),
                    render: (row) =>
                      signedMoney(row.signed_amount ?? row.amount, {
                        income: row.entry_type === 'income' || row.entry_type === 'ingreso',
                        isDarkMode,
                      }),
                  },
                ]}
                rows={movements.items || []}
              />
            )}
          </ColumnCard>
        </div>
      </div>
    </div>
  );
};

export default StakeholderDetailModal;
